import { useEffect, useState, useRef } from 'react';
import socketService from '../../../../../services/socketService';
import toast from 'react-hot-toast';
import { getCreatorDisplayName } from '../../../../../utils/creatorHelpers';

const useGroupSocket = (campaignId, callbacks) => {
 const [connected, setConnected] = useState(true);
 const callbacksRef = useRef(callbacks);
 callbacksRef.current = callbacks;

 useEffect(() => {
 if (!campaignId) return;

 socketService.joinCampaignRoom(campaignId);

 // Campaign updated - routes amplification-specific events
 const unsubCampaign = socketService.onCampaignUpdated(campaignId, (data) => {

 // Route amplification-specific events by type
 if (data.type === 'amplification:reaction:received') {
 if (callbacksRef.current.onNewReaction) {
 callbacksRef.current.onNewReaction(data.creator);
 }
 toast.success(`${getCreatorDisplayName(data.creator)} reacted to join!`);
 return;
 }

 if (data.type === 'amplification:reaction:synced') {
 if (callbacksRef.current.onReactionSync) {
 callbacksRef.current.onReactionSync();
 }
 toast.success(`Synced: ${data.added || 0} new creators added`);
 return;
 }

 if (data.type === 'amplification:reaction:unmatched') {
 toast(`Unmatched Discord user: ${data.discordUsername || 'unknown'}`, { icon: '⚠️' });
 return;
 }

 if (data.type === 'amplification:reaction:removed') {
 // Informational only - creator stays in pool
 return;
 }

 // Group lifecycle events
 if (data.type === 'amplification:group:created') {
 if (callbacksRef.current.onGroupCreated) {
 callbacksRef.current.onGroupCreated(data.group);
 }
 return;
 }

 if (data.type === 'amplification:group:activated') {
 if (callbacksRef.current.onGroupActivated) {
 callbacksRef.current.onGroupActivated(data.groupId, data.discord);
 }
 toast.success('Group activated - Discord resources created');
 return;
 }

 if (data.type === 'amplification:group:provisioning:complete') {
 if (callbacksRef.current.onProvisioningComplete) {
 callbacksRef.current.onProvisioningComplete(data.groupId, data.results);
 }
 const successCount = data.results?.success?.length || 0;
 const failedCount = data.results?.failed?.length || 0;
 if (failedCount > 0) {
 toast(`Provisioned ${successCount} creators, ${failedCount} failed`, { icon: '⚠️' });
 } else if (successCount > 0) {
 toast.success(`All ${successCount} creators provisioned to Discord`);
 }
 return;
 }

 if (data.type === 'amplification:group:provisioning:failed') {
 toast.error(`Group provisioning failed: ${data.error}`);
 return;
 }

 if (data.type === 'amplification:group:deactivated') {
 if (callbacksRef.current.onGroupDeactivated) {
 callbacksRef.current.onGroupDeactivated(data.groupId);
 }
 toast.success('Group deactivated');
 return;
 }

 if (data.type === 'amplification:group:creator:added' ||
 data.type === 'amplification:group:creator:removed') {
 // These are handled by the API call's optimistic updates
 // Refetch to ensure consistency
 if (callbacksRef.current.onReactionSync) {
 callbacksRef.current.onReactionSync();
 }
 return;
 }

 // Content monitoring events
 if (data.type === 'amplification:content:tweet_submitted') {
 if (callbacksRef.current.onTweetSubmitted) {
 callbacksRef.current.onTweetSubmitted(data.creatorId, data.tweet, data.groupId);
 }
 toast.success('Tweet submitted!');
 return;
 }

 if (data.type === 'amplification:content:activity') {
 if (callbacksRef.current.onContentActivity) {
 callbacksRef.current.onContentActivity(data.creatorId, data.groupId);
 }
 return;
 }

 // Default: generic campaign update
 if (callbacksRef.current.onCampaignUpdate) {
 callbacksRef.current.onCampaignUpdate(data);
 }
 });

 // Listen for creatorJoined events from regular Discord sync flow
 const handleCreatorJoined = (data) => {
 if (data.campaignId === campaignId && callbacksRef.current.onNewReaction) {
 const creator = data.creator || {};
 callbacksRef.current.onNewReaction({
 creatorId: creator._id,
 name: creator.name || creator.twitter?.username || '',
 twitterHandle: creator.twitter?.username || '',
 profileImage: creator.twitter?.profileImageUrl || '',
 discordId: creator.discord?.id || '',
 discordUsername: creator.discord?.username || ''
 });
 }
 };
	 const unsubCreatorJoined = socketService.on('creatorJoined', handleCreatorJoined);

 // Twitter sync completion - re-fetch to get updated metrics
 const handleBatchSynced = (data) => {
 if (data.campaignId === campaignId && callbacksRef.current.onReactionSync) {
 callbacksRef.current.onReactionSync();
 }
 };
	 const unsubBatchSynced = socketService.on('batch:synced', handleBatchSynced);

 // Connection status
 const handleConnect = () => setConnected(true);
 const handleDisconnect = () => setConnected(false);

	 const unsubConnect = socketService.onConnect(handleConnect);
	 const unsubDisconnect = socketService.onDisconnect(handleDisconnect);

 // Check initial connection
 setConnected(socketService.isConnected?.() ?? true);

 // Cleanup
	 return () => {
	 unsubCampaign();
	 unsubCreatorJoined();
	 unsubBatchSynced();
	 unsubConnect();
	 unsubDisconnect();
	 socketService.leaveCampaignRoom(campaignId);
	 };
 }, [campaignId]);

 return { connected };
};

export default useGroupSocket;
